import { useT, useLocalePath } from "@/i18n/LocaleContext";
import EditorialCard from "@/components/EditorialCard";
import EditorialFigure from "@/components/EditorialFigure";
import GlobalFootprint from "@/components/GlobalFootprint";
import CinematicHero from "@/components/CinematicHero";

const CARDS = [
  { id: "aifm",       image: "/images/card-aifm.jpg" },
  { id: "portfolio",  image: "/images/card-portfolio.jpg" },
  { id: "risk",       image: "/images/card-risk.jpg" },
  { id: "governance", image: "/images/card-governance.jpg" },
];

export const Home = () => {
  const { t } = useT();
  const localePath = useLocalePath();

  return (
    <>
      <CinematicHero
        imageSrc="/images/hero.jpg"
        imageAlt={t("hero_cinematic.home.alt")}
        eyebrow={t("hero_cinematic.home.eyebrow")}
        title={t("hero_cinematic.home.title")}
        subtitle={t("hero_cinematic.home.subtitle")}
        testIdPrefix="home-hero"
      />
      <div id="content" />

      {/* Regulatory Status */}
      <section data-testid="home-regulatory-status" className="bg-cream">
        <div className="mx-auto max-w-[1360px] px-6 md:px-10 lg:px-14 py-32 md:py-40">
          <div className="max-w-3xl cascade">
            <div className="reveal rule-gold w-16" aria-hidden />
            <div className="reveal flex items-center gap-4 mt-8">
              <span
                className="eyebrow font-sans text-[11px] font-medium uppercase text-navy/60"
                style={{ letterSpacing: "0.28em" }}
              >
                {t("home.reg_eyebrow")}
              </span>
            </div>
            <p
              data-testid="regulatory-status-body"
              className="reveal mt-8 md:mt-10 font-serif font-normal text-navy text-[22px] md:text-[27px] leading-[1.55]"
            >
              {t("home.reg_body")}
            </p>
          </div>
        </div>
      </section>

      {/* Editorial mid — marble column detail */}
      <section data-testid="home-midimage" className="bg-cream">
        <div className="mx-auto max-w-[1360px] px-6 md:px-10 lg:px-14 pb-16 md:pb-24">
          <EditorialFigure
            src="/images/home-mid.jpg"
            alt={t("home.mid_alt")}
            height="h-[46vh] min-h-[320px] max-h-[600px]"
            testId="home-mid-figure"
          />
        </div>
      </section>

      {/* Services — editorial cards */}
      <section data-testid="home-services" className="bg-cream">
        <div className="mx-auto max-w-[1360px] px-6 md:px-10 lg:px-14 pt-16 md:pt-24 pb-16 md:pb-24">
          <div className="cascade max-w-3xl">
            <div className="reveal flex items-center gap-4">
              <span className="h-px w-10 bg-gold" aria-hidden />
              <span
                className="eyebrow font-sans text-[11px] font-medium uppercase text-navy/60"
                style={{ letterSpacing: "0.28em" }}
              >
                {t("home.services_eyebrow")}
              </span>
            </div>
            <h2 className="reveal mt-8 md:mt-10 font-serif font-normal text-navy text-[32px] md:text-[44px] leading-[1.1]">
              {t("home.services_h2")}
            </h2>
          </div>

          <div
            className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10 reveal-stagger"
            role="list"
          >
            {CARDS.map((c) => (
              <div role="listitem" key={c.id}>
                <EditorialCard
                  to={localePath(`/services#${c.id}`)}
                  imageSrc={c.image}
                  imageAlt={t(`services_cards.${c.id}.image_alt`)}
                  eyebrow={t(`services_cards.${c.id}.eyebrow`)}
                  title={t(`services_cards.${c.id}.title`)}
                  line={t(`services_cards.${c.id}.line`)}
                  testId={`home-service-card-${c.id}`}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Institutional Framework — editorial paragraph + inset image (with parallax) */}
      <section data-testid="home-framework" className="bg-cream">
        <div className="mx-auto max-w-[1360px] px-6 md:px-10 lg:px-14 py-32 md:py-40">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
            <div className="md:col-span-5 cascade">
              <div className="reveal rule-gold w-16" aria-hidden />
              <div className="reveal mt-8 flex items-center gap-4">
                <span
                  className="eyebrow font-sans text-[11px] font-medium uppercase text-navy/60"
                  style={{ letterSpacing: "0.28em" }}
                >
                  {t("home.framework_eyebrow")}
                </span>
              </div>
              <p
                data-testid="framework-body"
                className="reveal mt-8 md:mt-10 font-serif font-normal text-navy text-[19px] md:text-[22px] leading-[1.65]"
              >
                {t("home.framework_body")}
              </p>
            </div>
            <div className="md:col-span-7 md:col-start-7">
              <div className="reveal mask-reveal">
                <div className="relative w-full h-[52vh] min-h-[340px] max-h-[640px] overflow-hidden">
                  <div className="absolute inset-0 parallax-inner">
                    <span className="reveal-scale">
                      <img
                        src="/images/home-framework.jpg"
                        alt={t("home.framework_image_alt")}
                        loading="lazy"
                        className="absolute inset-0 h-full w-full object-cover"
                      />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Global Footprint — quiet institutional world map */}
      <GlobalFootprint />

      {/* Section bridge — cream → navy fade into footer */}
      <div className="section-bridge-cream-navy" aria-hidden data-testid="home-bridge-footer" />
    </>
  );
};

export default Home;
